import * as Sentry from '@sentry/react'

/** Centralized API base URL — use this instead of defining API_BASE locally. */
export const API_BASE = import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api`
    : '/api'

/**
 * Wrapper fetch avec credentials et capture Sentry automatique.
 * Utiliser à la place de fetch() dans tous les hooks API.
 */
/**
 * Resolve an endpoint path through API_BASE.
 * Handles both "/api/catalog/formations" (strips /api prefix) and full URLs.
 */
export function resolveEndpoint(endpoint: string): string {
    if (endpoint.startsWith('/api/')) return `${API_BASE}${endpoint.slice(4)}`
    if (endpoint.startsWith('/api')) return `${API_BASE}${endpoint.slice(4)}`
    return endpoint
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    /*
     * 🔴 La résolution se fait ICI, au point de passage COMMUN — pas chez
     * l'appelant.
     *
     * En développement `VITE_API_URL` est délibérément absent : `API_BASE` vaut
     * `/api` et le proxy Vite renvoie vers le backend. Un chemin relatif y
     * marche donc, et c'est précisément ce qui rend la faute invisible. En
     * PRODUCTION le front est servi par un hébergeur de fichiers statiques
     * (`app.argalis.app`) et l'API vit ailleurs (`api.argalis.app`) : un chemin
     * relatif part sur le serveur statique, qui répond `405` à un POST et
     * `index.html` à un GET — d'où « La création a échoué. API error: 405 » et
     * « Impossible de charger », observés en prod le 2026-08-26.
     *
     * ⚠️ Le correctif du 2026-03-30 (94a1a8bb, « resolve DataTable server
     * endpoints through API_BASE for production routing ») avait déjà traité ce
     * défaut — mais dans `useDataTableServer` SEULEMENT. Les écrans montés sur
     * DataTable fonctionnaient donc, et tous les autres appels restaient
     * cassés : la liste des intervenants s'affichait pendant que sa création
     * échouait. Une règle posée sur un appelant laisse les vingt autres dehors.
     *
     * `resolveEndpoint` est IDEMPOTENT : une URL absolue (déjà résolue, ou
     * construite par l'appelant) ressort inchangée, et en dev `/api/x` rend
     * `/api/x`. Le double appel de `useDataTableServer` est donc sans effet.
     */
    const target = typeof input === 'string' ? resolveEndpoint(input) : input
    try {
        return await fetch(target, { credentials: 'include', ...init })
    } catch (err) {
        Sentry.captureException(err, {
            tags: { url: typeof target === 'string' ? target : target.toString() },
        })
        throw err
    }
}
